import {
  AuthenticationDetails,
  CodeDeliveryDetails,
  CognitoUser,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import appConfiguration from "../components/cfg/AppCfg";
import * as Sentry from "@sentry/react";

const { userPool } = appConfiguration;

const getTokenFingerprint = (token: string): string =>
  token ? `${token.slice(0, 12)}...${token.slice(-8)}` : "(none)";

interface ForgetPasswordResult {
  CodeDeliveryDetails: CodeDeliveryDetails;
}

export function forgotPassword(username: string): Promise<CodeDeliveryDetails> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: (data: ForgetPasswordResult) => {
        const { CodeDeliveryDetails } = data;
        resolve(CodeDeliveryDetails);
      },
      onFailure: (error) => {
        reject(error);
      },
    });
  });
}

export function confirmPassword(
  username: string,
  code: string,
  password: string,
) {
  return new Promise<void>((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.confirmPassword(code, password, {
      onSuccess: () => {
        resolve();
      },
      onFailure: (error) => {
        reject(error);
      },
    });
  });
}

export function signIn(username: string, password: string) {
  return new Promise((resolve, reject) => {
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        resolve(session);
      },
      onFailure: (error) => {
        reject(error);
      },
    });
  });
}

export const signOut = () => {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
};

export const isSignedIn = () => {
  const cognitoUser = userPool.getCurrentUser();
  return !!cognitoUser;
};

export const getAuthenticatedUser = () => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error("No user found"));
      return;
    }

    cognitoUser.getSession(
      (error: Error | null, session: CognitoUserSession | null) => {
        if (error) {
          reject(error);
          return;
        }
        cognitoUser.getUserAttributes((error, attributes) => {
          if (error) {
            reject(error);
            return;
          }
          const userData = (attributes ?? []).reduce(
            (
              acc: Record<string, string>,
              attribute: { Name: string; Value: string },
            ) => {
              acc[attribute.Name] = attribute.Value;
              return acc;
            },
            {},
          );
          resolve({
            ...userData,
            idToken: session?.getIdToken().getJwtToken(),
          });
        });
      },
    );
  });
};

export async function getUser() {
  const cognitoUser = userPool.getCurrentUser();
  return new Promise<CognitoUser>((resolve, reject) => {
    if (!cognitoUser) {
      reject(new Error("No user found"));
      return;
    }
    resolve(cognitoUser);
  });
}

export async function refreshSession() {
  Sentry.captureMessage("[AR-1810] refreshSession: START", {
    level: "info",
    tags: { ar1810: true },
  });
  const cognitoUser = await getUser();
  const session = await getSession();
  if (!session) {
    Sentry.captureMessage("[AR-1810] refreshSession: NO SESSION", {
      level: "warning",
      tags: { ar1810: true },
    });
    throw new Error("No session found");
  }
  const refreshToken = session.getRefreshToken();
  const refreshTokenFingerprint = refreshToken
    ? `${refreshToken.getToken().slice(0, 12)}...${refreshToken.getToken().slice(-8)}`
    : "(none)";
  Sentry.captureMessage(
    `[AR-1810] refreshSession: calling cognitoUser.refreshSession`,
    {
      level: "info",
      tags: { ar1810: true },
      extra: { refreshTokenFingerprint },
    },
  );
  return new Promise<CognitoUserSession>((resolve, reject) => {
    cognitoUser.refreshSession(refreshToken, (err, session) => {
      if (err) {
        Sentry.captureMessage("[AR-1810] refreshSession: FAILED", {
          level: "error",
          tags: { ar1810: true },
          extra: {
            errorName: err.name,
            errorMessage: err.message,
            errorCode: (err as { code?: string }).code,
          },
        });
        reject(err);
        return;
      }
      const newToken = session.getIdToken().getJwtToken();
      Sentry.captureMessage("[AR-1810] refreshSession: SUCCESS", {
        level: "info",
        tags: { ar1810: true },
        extra: { tokenFingerprint: getTokenFingerprint(newToken) },
      });
      resolve(session);
    });
  });
}

export function getSession() {
  const cognitoUser = userPool.getCurrentUser();
  return new Promise<CognitoUserSession | null>((resolve, reject) => {
    if (!cognitoUser) {
      reject(new Error("No user found"));
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(session);
      },
    );
  });
}

export async function getIdToken() {
  const session = await getSession();
  const token = session?.getIdToken().getJwtToken();
  if (!token) {
    Sentry.captureMessage("[AR-1810] getIdToken: NO TOKEN", {
      level: "warning",
      tags: { ar1810: true },
    });
    throw new Error("No token found");
  }
  Sentry.captureMessage("[AR-1810] getIdToken: SUCCESS", {
    level: "info",
    tags: { ar1810: true },
    extra: { tokenFingerprint: getTokenFingerprint(token) },
  });
  return token;
}
